import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Lightning, ArrowRight, Calculator, MagicWand, PaperPlaneTilt, Clock, FileText, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICONS = {
  upload: UploadSimple,
  magic: MagicWand,
  calculator: Calculator,
  letter: FileText,
  send: PaperPlaneTilt,
  clock: Clock,
};

const STATUS_BADGE = {
  eingang: { label: "Eingang", cls: "bg-[#FEF3C7] text-[#92400E]" },
  analyse: { label: "Analyse", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
  aktion: { label: "Aktion", cls: "bg-[#FCE7F3] text-[#9D174D]" },
  schreiben: { label: "Schreiben", cls: "bg-[#E0E7FF] text-[#3730A3]" },
  versand: { label: "Versand", cls: "bg-[#D1FAE5] text-[#065F46]" },
  fristen: { label: "Frist läuft", cls: "bg-[#FEF3C7] text-[#92400E]" },
  verfahren: { label: "Verfahren", cls: "bg-[#F3E8FF] text-[#6B21A8]" },
  offen: { label: "Offen", cls: "bg-[#F3F4F6] text-[#374151]" },
  in_bearbeitung: { label: "In Bearbeitung", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
};

export default function Aktionen() {
  const [empfehlungen, setEmpfehlungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("alle");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/aktionen/empfehlungen");
      setEmpfehlungen(r.data.empfehlungen || []);
    } catch {
      toast.error("Konnte Aktionen nicht laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter === "alle"
    ? empfehlungen
    : empfehlungen.filter(e => e.priority === filter);

  const counts = {
    hoch: empfehlungen.filter(e => e.priority === "hoch").length,
    normal: empfehlungen.filter(e => e.priority === "normal").length,
  };

  return (
    <div className="space-y-8" data-testid="aktionen-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#D32F2F] mb-2">Workflow · 4</p>
          <h1 className="font-serif text-3xl lg:text-4xl">Aktionen</h1>
          <p className="text-sm text-[#6B7280] mt-2 max-w-2xl">
            Was als nächstes zu tun ist. Für jede Akte zeigen wir den nächsten sinnvollen Schritt — ein Klick führt direkt in die Aufgabe.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <button onClick={() => setFilter("alle")} className={`px-3 py-1.5 rounded-md ${filter === "alle" ? "bg-[#1A2238] text-white" : "bg-white border border-[#E5E7EB]"}`} data-testid="aktionen-filter-alle">Alle ({empfehlungen.length})</button>
          <button onClick={() => setFilter("hoch")} className={`px-3 py-1.5 rounded-md ${filter === "hoch" ? "bg-[#D32F2F] text-white" : "bg-white border border-[#E5E7EB]"}`} data-testid="aktionen-filter-hoch">Dringend ({counts.hoch})</button>
          <button onClick={() => setFilter("normal")} className={`px-3 py-1.5 rounded-md ${filter === "normal" ? "bg-[#1A2238] text-white" : "bg-white border border-[#E5E7EB]"}`} data-testid="aktionen-filter-normal">Normal ({counts.normal})</button>
        </div>
      </header>

      {loading ? (
        <p className="text-sm text-[#9CA3AF]">Lade …</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-12 text-center">
          <Lightning size={32} weight="duotone" className="text-[#1A2238] mx-auto mb-3" />
          <p className="font-serif text-xl mb-1">Alles im Griff</p>
          <p className="text-sm text-[#6B7280]">Keine offenen Aktionen — alle aktiven Akten sind aktuell.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((e) => {
            const Icon = ICONS[e.next_action.icon] || ArrowRight;
            const badge = STATUS_BADGE[e.status] || STATUS_BADGE.offen;
            const dringend = e.priority === "hoch";
            return (
              <li key={e.akte_id} className={`bg-white border rounded-lg p-4 flex flex-wrap items-center gap-4 ${dringend ? "border-[#D32F2F]/30" : "border-[#E5E7EB]"}`} data-testid={`aktion-row-${e.akte_id}`}>
                <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${dringend ? "bg-[#FEE2E2] text-[#D32F2F]" : "bg-[#F3F4F6] text-[#1A2238]"}`}>
                  <Icon size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{e.aktenzeichen}</span>
                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>
                    {e.art === "owi" && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]">OWi</span>}
                  </div>
                  <p className="text-sm text-[#4B5563]">{e.mandant_name || "Mandant fehlt"}</p>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">
                    {e.stats.dokumente} Dok. · {e.stats.schriftsaetze} Schreiben · {e.stats.unversandt > 0 && <span className="text-[#D32F2F]">{e.stats.unversandt} unversandt · </span>}
                    {e.stats.unanalysiert > 0 && <span className="text-[#D32F2F]">{e.stats.unanalysiert} unanalysiert</span>}
                  </p>
                </div>
                <Button asChild className={dringend ? "bg-[#D32F2F] hover:bg-[#B91C1C]" : "bg-[#1A2238] hover:bg-[#2C3859]"} data-testid={`aktion-cta-${e.akte_id}`}>
                  <Link to={e.next_action.to}>{e.next_action.label} <ArrowRight size={14} className="ml-2" /></Link>
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
